import { FC, useEffect, useMemo } from "react";
import { observer } from "mobx-react";
import { useDispatch, useLanguage, useRootStore, useTranslation } from "@/stores/RootStoreContext";
import { AIClient } from "@/ai/AIAssistant";
import { AccountCompanyDetails } from "@/common/common";
import { Box, VBox } from "@/components-v2/Box";
import { Button } from "@/components-v2/Button";
import { DataTable, TableColumn } from "@/components-v2/DataTable";
import { EmptyState } from "@/components-v2/EmptyState";
import { Icon } from "@/components-v2/Icon";
import { ResizableSidepanelLayout } from "@/components-v2/ResizableSidepanelLayout";
import { MTextInput } from "@/components-v2/TextInput";
import { DashboardCardDefinition, DashboardOverview } from "./dashboardApiClient";
import {
  cardCreatedLabel,
  cardTypeLabel,
  cardUsageLabel,
  dashboardStatusLabel,
  dashboardUpdatedLabel,
  dashboardRowName,
  DashboardRow,
  dashboardRows,
  dashboardVisibleToLabel,
  dashboardSettingsTitle,
  DashboardSettingsState,
  DashboardSettingsTab,
  initDashboardSettings,
  prepCardActions,
  prepDashboardActions,
  prepDashboardSettingsEmptyState,
  prepDashboardSettingsSearch,
  prepNewButton,
  prepSystemDefaultActions,
  selectItem,
  systemDefaultVisibleToLabel,
} from "./dashboardSettingsDomain";
import { DashboardSettingsDialogView } from "./dashboardDialogsComponents";
import { DashboardPreviewPanel } from "./dashboardPanelComponents";

export const DashboardSettingsSection: FC<{
  accountId: string;
  companies: AccountCompanyDetails[];
  tab: DashboardSettingsTab;
  hasTemplates: boolean;
}> = observer(({ accountId, companies, tab, hasTemplates }) => {
  const rootStore = useRootStore();
  const dispatch = useDispatch();
  const language = useLanguage();
  useEffect(() => {
    dispatch(
      initDashboardSettings(
        accountId,
        language,
        tab,
        hasTemplates,
        companies.map((company) => ({
          orgNo: company.orgNo,
          companyId: company.companyId,
          name: company.companyName,
        })),
      ),
    );
  }, [dispatch, accountId, language, tab, hasTemplates, companies]);
  const state = rootStore.dashboardSettings;
  return state?.accountId === accountId ? <DashboardSettingsView state={state} /> : null;
});

const DashboardSettingsView: FC<{ state: DashboardSettingsState }> = observer(({ state }) => {
  const { t } = useTranslation();
  const { http } = useRootStore();
  const aiClient = useMemo(() => new AIClient(http), [http]);
  const emptyState = prepDashboardSettingsEmptyState(state, aiClient);
  return (
    <>
      <ResizableSidepanelLayout
        containerProps={{ fullheight: true, overflow: "hidden" }}
        rightBaseSizePercent={70}
        centerPanel={
          <VBox grow alignItems={"stretch"}>
            <VBox bg={"default"} borderBottom alignItems={"stretch"}>
              <VBox
                container
                maxWidth={"lg"}
                paddingX={"lg"}
                paddingTop={"lg"}
                alignItems={"stretch"}
              >
                <Box
                  justifyContent={"space-between"}
                  alignItems={"start"}
                  gap={"md"}
                  paddingBottom={"lg"}
                  wrap
                >
                  <h3>{t(dashboardSettingsTitle[state.tab])}</h3>
                  {!emptyState && <Button {...prepNewButton(state, aiClient)} />}
                </Box>
              </VBox>
            </VBox>
            <Box alignItems={"stretch"} grow>
              <VBox container padding={"lg"} gap={"lg"} maxWidth={"lg"} alignItems={"stretch"} grow>
                {!emptyState && (
                  <Box>
                    <MTextInput {...prepDashboardSettingsSearch(state)} />
                  </Box>
                )}
                {state.overview &&
                  !emptyState &&
                  (state.tab === "dashboards" ? (
                    <DashboardsTable state={state} overview={state.overview} aiClient={aiClient} />
                  ) : (
                    <CardsTable state={state} cards={state.overview.cards} aiClient={aiClient} />
                  ))}
                {emptyState && <EmptyState {...emptyState} />}
              </VBox>
            </Box>
          </VBox>
        }
        rightPanel={
          state.selected && <DashboardPreviewPanel state={state} selected={state.selected} />
        }
      />
      <DashboardSettingsDialogView state={state} />
    </>
  );
});

const DashboardsTable: FC<{
  state: DashboardSettingsState;
  overview: DashboardOverview;
  aiClient: AIClient;
}> = observer(({ state, overview, aiClient }) => {
  const tableColumns: TableColumn<DashboardRow>[] = [
    {
      name: "title",
      align: "left",
      label: { key: "title" },
      getFilterValue: (row, _, { t }) => dashboardRowName(row, t),
      getSortValue: (row, _, { t }) => dashboardRowName(row, t),
      getValue: (row, _, { t }) => (
        <Box alignItems={"center"} gap={"md"}>
          <Icon name={row.kind === "dashboard" ? "tabs" : "lock"} size={"lg"} />
          <VBox>
            <div>{dashboardRowName(row, t)}</div>
            <Box fg={"subtle"} textSize={"sm"}>
              {t(
                dashboardStatusLabel(
                  row.kind === "dashboard" ? row.dashboard : { status: row.status },
                ),
              )}
            </Box>
          </VBox>
        </Box>
      ),
    },
    {
      name: "visibleTo",
      align: "left",
      label: { key: "enterprise-account.dashboards.column.visible-to" },
      notSorted: true,
      getValue: (row) =>
        row.kind === "dashboard"
          ? dashboardVisibleToLabel(overview, row.dashboard)
          : systemDefaultVisibleToLabel(row.status),
    },
    {
      name: "updatedAt",
      align: "left",
      label: { key: "enterprise-account.dashboards.column.last-updated" },
      getSortValue: (row) => (row.kind === "dashboard" ? row.dashboard.updatedAt : undefined),
      getValue: (row) => (row.kind === "dashboard" ? dashboardUpdatedLabel(row.dashboard) : ""),
    },
  ];
  return (
    <DataTable
      tableColumns={tableColumns}
      tableData={dashboardRows(overview)}
      getId={(row) => (row.kind === "dashboard" ? row.dashboard.id : "system-default")}
      filter={state.search.inputValue}
      onRowClick={(row) =>
        row.kind === "dashboard"
          ? selectItem(state, { kind: "dashboard", id: row.dashboard.id })
          : []
      }
      rowActions={(row) =>
        row.kind === "dashboard"
          ? prepDashboardActions(state, aiClient, row.dashboard)
          : prepSystemDefaultActions(state, row.status)
      }
    />
  );
});

const CardsTable: FC<{
  state: DashboardSettingsState;
  cards: DashboardCardDefinition[];
  aiClient: AIClient;
}> = observer(({ state, cards, aiClient }) => {
  const tableColumns: TableColumn<DashboardCardDefinition>[] = [
    {
      name: "title",
      align: "left",
      label: { key: "title" },
      getFilterValue: (row) => row.internalName,
      getSortValue: (row) => row.internalName,
      getValue: (row, _, { t }) => (
        <Box alignItems={"center"} gap={"md"}>
          <Icon name={"squares-four"} size={"lg"} />
          <VBox>
            <div>{row.internalName}</div>
            <Box fg={"subtle"} textSize={"sm"}>
              {t(cardUsageLabel(row))}
            </Box>
          </VBox>
        </Box>
      ),
    },
    {
      name: "type",
      align: "left",
      label: { key: "type" },
      getValue: (row) => cardTypeLabel(row),
    },
    {
      name: "createdAt",
      align: "left",
      label: { key: "enterprise-account.dashboards.column.created" },
      getSortValue: (row) => row.createdAt,
      getValue: (row) => cardCreatedLabel(row),
    },
  ];
  return (
    <DataTable
      tableColumns={tableColumns}
      tableData={cards}
      getId={(row) => row.id}
      filter={state.search.inputValue}
      onRowClick={(row) => selectItem(state, { kind: "card", id: row.id })}
      rowActions={(row) => prepCardActions(state, aiClient, row)}
    />
  );
});
